import React, { useState, useRef, useEffect, useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { HiOutlinePlus } from "react-icons/hi";
import API from "../api";
import { EXAM_TYPE_COLOR_BG, EXAM_TYPE_COLOR_TEXT, EXAM_TYPE_COLOR_BORDER } from "../utils";
import { capture } from "../instrument";

const MIN_QUERY_LENGTH = 1;

const CodeLookup = ({ onChange, initialValues = [], placeholder = "Séparer les valeurs par une virgule (,)", allowManualEntry = true }) => {
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionItemsRef = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  // Initialize tags from initialValues — auto-detect string vs object format
  useEffect(() => {
    if (!initialValues?.length) return setTags([]);
    const normalized = initialValues.map((item) => (typeof item === "string" ? { value: item } : item)); // TODO : remove this when trigger and rules use _id of code and not code value
    setTags(normalized);
  }, [initialValues]);

  const { data: allCodes = [] } = useQuery({
    queryKey: ["codes"],
    queryFn: async () => {
      const response = await API.get("/code");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: tags.some((t) => !t.codeType),
  });

  const codeTypeByValue = useMemo(() => Object.fromEntries(allCodes.map((c) => [c.value, c.codeType])), [allCodes]);

  // Auto-scroll to keep selected item in view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionItemsRef.current[selectedIndex]) {
      // Use scrollIntoView for more reliable scrolling, especially in modals
      suggestionItemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Reset refs array when suggestions change
  useEffect(() => {
    suggestionItemsRef.current = suggestionItemsRef.current.slice(0, codeSuggestions.length);
  }, [codeSuggestions]);

  const fetchCodeSuggestions = async (query) => {
    if (!query || query.length < MIN_QUERY_LENGTH) {
      setCodeSuggestions([]);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setIsLoading(true);

      const encodedQuery = encodeURIComponent(query);
      const filterOutCodes = tags.map((t) => t.value).join(",");
      const response = await API.get(`/code?q=${encodedQuery}&filterOutCodes=${filterOutCodes}`, { signal });

      if (signal.aborted) return;
      if (!response.ok) return toast.error(response.message);

      setCodeSuggestions(response.data);
      setShowSuggestions(true);
      setSelectedIndex(0);
    } catch (error) {
      if (error.name === "AbortError") return;
      capture(error);

      console.log(error);
      toast.error("Une erreur est survenue");
      return [];
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  // Add debounce for better performance
  useEffect(() => {
    if (inputValue?.length < MIN_QUERY_LENGTH) return;

    const debounceTimer = setTimeout(() => fetchCodeSuggestions(inputValue), 200);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, tags]);

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Check if the input ends with a comma
    if (!value.endsWith(",")) return setInputValue(value);

    if (!allowManualEntry) return setInputValue(value.slice(0, -1));

    const newValue = value.slice(0, -1).trim();
    if (!newValue) return setInputValue("");
    if (tags.some((t) => t.value === newValue)) {
      toast.error("Le code est déjà présent");
      return setInputValue("");
    }

    const newTag = { value: newValue };
    const newTags = [...tags, newTag];
    setTags(newTags);
    if (onChange) onChange(newTags);

    setInputValue("");
    setCodeSuggestions([]);
    setShowSuggestions(false);
  };

  const addTag = (tagObject) => {
    if (tags.some((t) => t.value === tagObject.value)) return toast.error("Le code est déjà présent");

    if (tagObject.value) {
      const newTags = [...tags, tagObject];
      setTags(newTags);
      if (onChange) onChange(newTags);
    }

    setInputValue("");
    setCodeSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Focus back on the input
    if (inputRef.current) inputRef.current.focus();
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter((t) => t.value !== tagToRemove.value);
    setTags(newTags);
    if (onChange) onChange(newTags);
    if (inputRef.current) inputRef.current.focus();
  };

  const trimmedInput = inputValue.trim();
  const canCreate = allowManualEntry && !!trimmedInput && !codeSuggestions.some((s) => s.value.toLowerCase() === trimmedInput.toLowerCase());
  const options = canCreate ? [...codeSuggestions, { _id: "create", value: trimmedInput, isNew: true }] : codeSuggestions;
  const isOpen = showSuggestions && options.length > 0;
  const highlightedIndex = Math.min(Math.max(selectedIndex, 0), options.length - 1);
  const selectOption = (option) => addTag(option.isNew ? { value: option.value } : option);

  const navOpts = { enabled: isOpen, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, options.length - 1)), navOpts, [isOpen, options.length]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [isOpen, options.length]);
  useHotkeys("enter", () => selectOption(options[highlightedIndex]), navOpts, [isOpen, options, highlightedIndex]);
  useHotkeys(
    "escape",
    () => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    },
    navOpts,
    [isOpen],
  );

  // Tag editing only — suggestion navigation is handled above while the dropdown is open.
  const handleKeyDown = (e) => {
    if (isOpen) return;
    if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    } else if (e.key === "Enter" && inputValue.trim() && allowManualEntry) {
      e.preventDefault();
      addTag({ value: inputValue.trim() });
    }
  };

  const handleContainerClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="flex min-h-[42px] cursor-text flex-wrap items-center gap-1 rounded-lg border bg-white p-2 transition-all duration-200"
        onClick={handleContainerClick}
      >
        {tags.map((tag, idx) => {
          const codeType = tag.codeType || codeTypeByValue[tag.value];
          const chipVariant =
            codeType === "groupe"
              ? "border-blue-200 bg-blue-50 text-blue-700"
              : codeType
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-primary-200 bg-primary-100 text-primary-800";
          return (
            <div key={idx} className={`flex items-center rounded-md border px-1 py-0.5 text-sm ${chipVariant}`}>
              {tag.value}
              <button
                className="ml-1 opacity-60 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                type="button"
                aria-label={`Remove ${tag.value}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          );
        })}
        <div className="flex flex-grow items-center">
          <input
            ref={inputRef}
            className="min-w-[120px] flex-grow outline-none"
            placeholder={tags.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={() =>
              setTimeout(() => {
                setShowSuggestions(false);
                setSelectedIndex(-1);
              }, 200)
            }
            onFocus={() => {
              if (inputValue.length >= MIN_QUERY_LENGTH) {
                fetchCodeSuggestions(inputValue);
              }
            }}
          />
          {isLoading && <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>}
        </div>
      </div>

      {isOpen && (
        <div ref={suggestionsRef} className="absolute z-10 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {options.map((suggestion, idx) => (
            <div
              ref={(el) => (suggestionItemsRef.current[idx] = el)}
              key={suggestion._id}
              className={`flex cursor-pointer items-center justify-between border-b border-gray-100 p-3 last:border-b-0 ${
                highlightedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
              }`}
              onMouseDown={() => selectOption(suggestion)}
            >
              {suggestion.isNew ? (
                <div className="flex items-center gap-2">
                  <HiOutlinePlus className="h-4 w-4 text-primary-500" />
                  <span className="text-sm">
                    Créer le code <span className="font-medium">{suggestion.value}</span>
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{suggestion.value}</span>
                  {suggestion.name && <span className="text-xs text-gray-500">{suggestion.name}</span>}
                  <span
                    className={`text-xs ${EXAM_TYPE_COLOR_BG[suggestion.type]} ${EXAM_TYPE_COLOR_TEXT[suggestion.type]} ${
                      EXAM_TYPE_COLOR_BORDER[suggestion.type]
                    } rounded-md px-2 py-1`}
                  >
                    {suggestion.type}
                  </span>
                  {suggestion.codeType && (
                    <span
                      className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
                        suggestion.codeType === "groupe" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {suggestion.codeType === "groupe" ? "G" : "A"}
                    </span>
                  )}
                  {suggestion.examValue && <span className="text-xs text-gray-500">{suggestion.examValue}</span>}
                </div>
              )}
              {highlightedIndex === idx && <div className="rounded bg-primary-100 px-2 py-1 text-xs font-medium text-primary-500">Enter ↵</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CodeLookup;
